import React from "react";
import { Skeleton, Stack, Box } from "@mui/material";

type SkeletonLoaderVariant = "text" | "rectangular" | "rounded" | "circular";
type SkeletonLayout = "default" | "page" | "form" | "cards";

type SkeletonLoaderProps = {
  lines?: number;
  height?: string | number;
  width?: string | number;
  variant?: SkeletonLoaderVariant;
  layout?: SkeletonLayout;
};

const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({
  lines = 3,
  height = 20,
  width = "100%",
  variant = "text",
  layout = "default",
}) => {
  const renderDefault = () => (
    <Stack spacing={1}>
      {Array.from({ length: lines }).map((_, index) => (
        <Skeleton
          key={index}
          variant={variant}
          sx={{ width, height }}
          animation="wave"
          data-testid="skeleton-element"
        />
      ))}
    </Stack>
  );

  const renderPage = () => (
    <Box
      sx={{
        px: 4,
        py: 4, // Add top and bottom padding
      }}
    >
      <Stack spacing={2} width="100%" maxWidth="800px">
        <Skeleton
          variant="text"
          height={40}
          width="60%"
          data-testid="skeleton-element"
        />
        <Skeleton
          variant="text"
          height={25}
          width="80%"
          data-testid="skeleton-element"
        />
        <Skeleton
          variant="rectangular"
          height={300}
          data-testid="skeleton-element"
        />
        <Stack direction="row" spacing={2}>
          <Skeleton
            variant="rounded"
            height={100}
            width="100%"
            data-testid="skeleton-element"
          />
          <Skeleton
            variant="rounded"
            height={100}
            width="100%"
            data-testid="skeleton-element"
          />
        </Stack>
      </Stack>
    </Box>
  );

  const renderForm = () => (
    <Stack spacing={2} width="100%">
      {[...Array(lines)].map((_, i) => (
        <Box key={i}>
          <Skeleton variant="text" width="40%" height={20} data-testid="skeleton-element" />
          <Skeleton variant="rectangular" width="100%" height={45} data-testid="skeleton-element" />
        </Box>
      ))}
    </Stack>
  );

  const renderCards = () => (
    <Stack spacing={2}>
      {[...Array(lines)].map((_, i) => (
        <Skeleton
          key={i}
          variant="rectangular"
          height={150}
          sx={{ borderRadius: 2 }}
          data-testid="skeleton-element"
        />
      ))}
    </Stack>
  );

  const layoutMap: Record<SkeletonLayout, () => JSX.Element> = {
    default: renderDefault,
    page: renderPage,
    form: renderForm,
    cards: renderCards,
  };

  return layoutMap[layout]();
};

export default SkeletonLoader;
